import { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Layout, Search, Grid3X3, 
  LayoutGrid, LayoutList, Copy, Check, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/lib/api';

const CATEGORIES = [
  { id: 'hero', name: 'Hero Sections', icon: Layout },
  { id: 'features', name: 'Features/Benefits', icon: Grid3X3 },
  { id: 'cta', name: 'Call to Action', icon: LayoutGrid },
  { id: 'testimonials', name: 'Testimonials', icon: LayoutList },
  { id: 'pricing', name: 'Pricing', icon: LayoutGrid },
  { id: 'faq', name: 'FAQ', icon: LayoutList },
  { id: 'stats', name: 'Statistics', icon: Grid3X3 },
  { id: 'team', name: 'Team', icon: LayoutGrid },
  { id: 'contact', name: 'Contact', icon: LayoutList },
  { id: 'custom', name: 'Custom', icon: Package }
];

const AdminComponentLibrary = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'hero',
    description: '',
    thumbnail_url: '',
    default_content: {},
    schema: {},
    html_template: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await adminApi.getComponentTemplates();
      setTemplates(res.data || []);
    } catch (error) {
      toast.error('Failed to load component templates');
    } finally {
      setLoading(false);
    }
  };

  const seedTemplates = async () => {
    try {
      await adminApi.seedComponentTemplates();
      toast.success('Component templates seeded successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to seed templates');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // Parse JSON fields
    let default_content = {};
    let schema = {};
    
    try {
      if (typeof formData.default_content === 'string' && formData.default_content) {
        default_content = JSON.parse(formData.default_content);
      } else {
        default_content = formData.default_content || {};
      }
    } catch (e) {
      toast.error('Invalid JSON in Default Content');
      return;
    }
    
    try {
      if (typeof formData.schema === 'string' && formData.schema) {
        schema = JSON.parse(formData.schema);
      } else {
        schema = formData.schema || {};
      }
    } catch (e) {
      toast.error('Invalid JSON in Schema');
      return;
    }
    
    const submitData = {
      ...formData,
      default_content,
      schema
    };
    
    try {
      if (editingTemplate) {
        await adminApi.updateComponentTemplate(editingTemplate.id, submitData);
        toast.success('Component template updated');
      } else {
        await adminApi.createComponentTemplate(submitData);
        toast.success('Component template created');
      }
      setModalOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save component template');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteComponentTemplate(id);
      toast.success('Component template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete component template');
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      category: 'hero',
      description: '',
      thumbnail_url: '',
      default_content: {},
      schema: {},
      html_template: '',
      is_active: true
    });
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      description: template.description || '',
      thumbnail_url: template.thumbnail_url || '',
      default_content: typeof template.default_content === 'object' 
        ? JSON.stringify(template.default_content, null, 2) 
        : template.default_content || '{}',
      schema: typeof template.schema === 'object' 
        ? JSON.stringify(template.schema, null, 2) 
        : template.schema || '{}',
      html_template: template.html_template || '',
      is_active: template.is_active !== false
    });
    setModalOpen(true);
  };

  const duplicateTemplate = async (template) => {
    const newTemplate = {
      ...template,
      name: `${template.name} (Copy)`,
      id: undefined,
      created_at: undefined,
      updated_at: undefined
    };
    
    try {
      await adminApi.createComponentTemplate(newTemplate);
      toast.success('Component template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const category = CATEGORIES.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      hero: 'bg-blue-100 text-blue-800',
      features: 'bg-green-100 text-green-800',
      cta: 'bg-orange-100 text-orange-800',
      testimonials: 'bg-purple-100 text-purple-800',
      pricing: 'bg-yellow-100 text-yellow-800',
      faq: 'bg-pink-100 text-pink-800',
      stats: 'bg-cyan-100 text-cyan-800',
      team: 'bg-indigo-100 text-indigo-800',
      contact: 'bg-red-100 text-red-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return colors[categoryId] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-component-library">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Component Library</h1>
          <p className="text-muted-foreground">Pre-built sections for the page builder</p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={seedTemplates}>
              <Package className="w-4 h-4 mr-2" /> Load Default Templates
            </Button>
          )}
          <Button onClick={() => { resetForm(); setModalOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Add Component
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search components..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex border rounded-lg p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <LayoutList className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Category Stats */}
      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map((cat) => {
          const count = templates.filter(t => t.category === cat.id).length;
          return (
            <Badge 
              key={cat.id}
              variant="outline" 
              className={`cursor-pointer ${selectedCategory === cat.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
            >
              {cat.name} ({count})
            </Badge>
          );
        })}
      </div>

      {/* Components Grid/List */}
      {filteredTemplates.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Components Found</h3>
            <p className="text-muted-foreground mb-4">
              {templates.length === 0 
                ? 'Load default templates or create your first component'
                : 'Try adjusting your search or filter'
              }
            </p>
            {templates.length === 0 && (
              <Button onClick={seedTemplates}>
                <Package className="w-4 h-4 mr-2" /> Load Default Templates
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              {template.thumbnail_url ? (
                <div className="aspect-video bg-muted relative">
                  <img 
                    src={template.thumbnail_url} 
                    alt={template.name}
                    className="w-full h-full object-cover"
                  />
                  {!template.is_active && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Badge variant="secondary">Inactive</Badge>
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-video bg-muted flex items-center justify-center">
                  <Layout className="w-12 h-12 text-muted-foreground" />
                </div>
              )}
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{template.name}</h3>
                    <Badge className={`${getCategoryColor(template.category)} mt-1`}>
                      {getCategoryName(template.category)}
                    </Badge>
                  </div>
                </div>
                {template.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{template.description}</p>
                )}
              </CardContent>
              <CardFooter className="border-t pt-4 flex justify-between">
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(template)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => duplicateTemplate(template)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Component</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{template.name}"?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(template.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTemplates.map((template) => (
            <Card key={template.id}>
              <CardContent className="py-4 flex items-center gap-4">
                {template.thumbnail_url ? (
                  <img 
                    src={template.thumbnail_url} 
                    alt={template.name}
                    className="w-24 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-24 h-16 bg-muted rounded flex items-center justify-center">
                    <Layout className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{template.name}</h3>
                    <Badge className={getCategoryColor(template.category)}>
                      {getCategoryName(template.category)}
                    </Badge>
                    {!template.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  {template.description && (
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(template)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => duplicateTemplate(template)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Component</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{template.name}"?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(template.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Component' : 'Create Component'}</DialogTitle>
            <DialogDescription>Define a reusable section template for the page builder</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="content">Default Content</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Component Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Hero Banner with Video"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this component..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Thumbnail URL</Label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(c) => setFormData({ ...formData, is_active: c })}
                />
                <Label>Active (available in page builder)</Label>
              </div>
            </TabsContent>
            
            <TabsContent value="content" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Default Content (JSON)</Label>
                <Textarea
                  value={typeof formData.default_content === 'string' 
                    ? formData.default_content 
                    : JSON.stringify(formData.default_content, null, 2)}
                  onChange={(e) => setFormData({ ...formData, default_content: e.target.value })}
                  placeholder='{"title": "Welcome", "subtitle": "..."}'
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Define the default content structure for this component
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="advanced" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Schema (JSON Schema for validation)</Label>
                <Textarea
                  value={typeof formData.schema === 'string' 
                    ? formData.schema 
                    : JSON.stringify(formData.schema, null, 2)}
                  onChange={(e) => setFormData({ ...formData, schema: e.target.value })}
                  placeholder='{"type": "object", "properties": {...}}'
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label>HTML Template (Optional)</Label>
                <Textarea
                  value={formData.html_template}
                  onChange={(e) => setFormData({ ...formData, html_template: e.target.value })}
                  placeholder="<section>...</section>"
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Optional HTML template for custom rendering
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editingTemplate ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminComponentLibrary;
